/**
 * "Customize these relationships" — the record surface's own rung (#1466 B4).
 *
 * The SAME `NavigationEditor` the navigation bar is shaped with, over a
 * one-group document whose items are relationship SLOTS. Not a second editor
 * with a different reorder/hide vocabulary: `ui-and-experience.md` §1's whole
 * argument is that two of one thing is the cost, and reorder-and-hide over a
 * list of named things is a grammar this repo has already paid to get right.
 *
 * It renders IN PLACE where the live Connections surface was; there is no
 * detached sheet to cover the record being customized. What differs from the
 * navigation host is only which door the save goes through and where the base
 * comes from:
 *
 *   the navigation bar  → `core.pin_navigation(document | null)`, base = the bar
 *   these related types → `core.set_user_setting('related_order', …)`,
 *                         base = `listSlots(schema, entity)` — the slots of the
 *                         record you are looking at
 *
 * ── The base comes from the SURFACE, always (owner ruling M3) ──────────────
 * `packages/ui` imports no base, knows no entity key and fetches nothing, so
 * the host is the only place that can say what exists. That is not ceremony:
 * `NavigationPanel.tsx:41-44` feeds the editor the CONSOLE's bar whatever host
 * it is editing, and because `rowsFor` filters to base, a portal document opened
 * there is rewritten to console routes on Save. Handing the editor the slots of
 * some other entity would be the same defect with relationship ids in it.
 *
 * ── Labels are the metamodel's, and there is no map here (#1705) ───────────
 * A row is named by `slot.label` — `relationship_type.label` for an outbound
 * reading, `inverse_label` for an inbound one, resolved once in `relations.ts`.
 * This file adds no icon table, no plural-label map and no per-module fallback;
 * where an identity is needed beyond what a slot already carries, it comes from
 * the shared identity API, never from a second table here.
 *
 * ── O5: hide is allowed, and a hidden type stays restorable ────────────────
 * A related type is presentation — RLS still governs every row behind it — and
 * refusing hide here while allowing it for nav items would be two rules for one
 * grammar. It stays restorable because `NavigationEditor` carries hidden entries
 * IN the document rather than deleting them, which is the one property that
 * stops personalisation becoming a trapdoor.
 */
import { useState, type RefObject } from 'react'
import { isNavDocument, NavigationEditor, reconcile, Select, useToast } from '@solum/ui'
import type { RelationSlot } from './relations'
import {
  relatedBase, useClearRelatedOrderDefault, useRelatedOrder, useRelatedOrderDefaults,
  useSetRelatedOrderDefault,
} from './useRelatedOrder'
import { useExperienceAtHost } from '../data/queries'
import { usePersona } from '../app/persona'
import { readableError } from '@solum/sdk'

export function PersonalizeRelated({ entity, entityLabel, slots, onClose, restoreFocusTo }: {
  /** The record's own entity — the statement is per from-entity, like the counts. */
  entity: string
  /** How to name the thing being personalised, in the editor's own copy. */
  entityLabel: string
  /** The slots this surface would show with no statement — the authority on what exists. */
  slots: RelationSlot[]
  onClose: () => void
  restoreFocusTo?: RefObject<HTMLElement | null>
}) {
  const order = useRelatedOrder(entity)
  const [layer, setLayer] = useState<'user' | 'persona'>('user')
  const [host, setHost] = useState<'console' | 'portal'>('console')
  const persona = usePersona()
  /* A successful inventory is the authority probe. A regular user receives
     42501 and gets no shared-default affordance; their own editor still works. */
  const defaults = useRelatedOrderDefaults(true)
  const shared = useExperienceAtHost(entity, 'related', host,
    layer === 'persona' && defaults.isSuccess)
  const setDefault = useSetRelatedOrderDefault()
  const clearDefault = useClearRelatedOrderDefault()
  const toast = useToast()
  const structural = relatedBase(slots)
  /* Without a personal statement, the resolved platform/persona arrangement
     is the editor's inherited base. With one, the metamodel is the base so the
     caller may restore a type their persona hid (ruling A2). */
  const userBase = order.isCustomised ? structural : reconcile(order.document, structural)
  const exact = (defaults.data ?? []).find((candidate) =>
    candidate.entity_type === entity && candidate.persona === persona && candidate.host === host)
  const inherited = isNavDocument(shared.data) ? shared.data : structural
  const base = layer === 'user' ? userBase : structural
  const value = layer === 'user' ? order.ownDocument : (exact?.document ?? inherited)
  const isCustomised = layer === 'user' ? order.isCustomised : Boolean(exact)

  const close = () => {
    onClose()
    queueMicrotask(() => restoreFocusTo?.current?.focus())
  }

  const saveOwn = (doc: Parameters<typeof order.save>[0]) => order.save(doc, {
    onSuccess: () => {
      toast.success('Related types saved', 'Only you see this. Reset it any time from here.')
      close()
    },
    onError: (e) => toast.error('Couldn’t save your related types', readableError(e)),
  })

  const save = (doc: Parameters<typeof order.save>[0]) => {
    if (!doc) return
    if (layer === 'user') {
      saveOwn(doc)
      return
    }
    setDefault.mutate({ entity, persona, host, document: doc }, {
      onSuccess: () => {
        toast.success('Persona related types saved', `${persona} now receives this ${host} arrangement by default.`)
        close()
      },
      onError: (e) => toast.error('Could not save the persona related types', readableError(e)),
    })
  }

  const reset = () => {
    if (layer === 'user') {
      saveOwn(null)
      return
    }
    clearDefault.mutate({ entity, persona, host }, {
      onSuccess: () => {
        toast.success('Persona related types reset', `The ${persona} ${host} arrangement now inherits again.`)
        close()
      },
      onError: (e) => toast.error('Could not reset the persona related types', readableError(e)),
    })
  }

  return (
    <section aria-label={`Customize the relationships on ${entityLabel}`}
      data-testid="customize-related-in-place">
      <NavigationEditor
        key={`${layer}.${persona}.${host}`}
        base={base}
        value={value}
        scope={layer}
        scopeLabel={layer === 'user'
          ? 'your related types'
          : `the ${persona} persona's ${host === 'portal' ? 'customer portal' : 'console'} related types`}
        isCustomised={isCustomised}
        busy={order.busy || setDefault.isPending || clearDefault.isPending
          || (layer === 'persona' && (!defaults.isSuccess || !shared.isSuccess))}
        /* Same reconciliation as `RecordBody`: a live tab with no persona
           chosen yet (#1053 4b) has no persona layer to edit, and
           `core.set_related_order` refuses an unknown persona
           (`packages/core/migrations/2026090610:38`). */
        layerControl={defaults.isSuccess && persona !== '' ? (
          <div data-testid="related-layer-controls">
            <Select
              value={layer}
              aria-label="Customization layer"
              options={[
                { value: 'user', label: 'Only me' },
                { value: 'persona', label: `${persona} persona` },
              ]}
              onValueChange={(next) => setLayer(next as 'user' | 'persona')}
            />
            {layer === 'persona' && (
              <Select
                value={host}
                aria-label="Experience host"
                options={[
                  { value: 'console', label: 'Console' },
                  { value: 'portal', label: 'Customer portal' },
                ]}
                onValueChange={(next) => setHost(next as 'console' | 'portal')}
              />
            )}
          </div>
        ) : undefined}
        onSave={save}
        /* Reset hands the order back to persona/platform inheritance (and then
           the learned signal when no authored rung exists), not to a blank list. Absent
           would mean "nothing to undo", which is wrong the moment a statement
           exists. */
        {...(isCustomised ? { onReset: reset } : {})}
        resetLabel="Reset to inherited related types"
        onCancel={close}
      />
    </section>
  )
}
